import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, ChevronDown, ChevronRight, Play, XCircle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/hr/payroll/runs/$runId')({
  component: RunDetailPage,
})

function RunDetailPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <RunDetail />
    </ModuleGuard>
  )
}

type PayslipComponent = {
  name?: string
  code?: string
  type?: string
  amount?: number
  calculationType?: string
}

type PayslipRow = {
  payslip: {
    id: number
    employeeId: number
    status: string
    baseAmount?: string | null
    gross?: string | null
    totalDeductions?: string | null
    netPay?: string | null
    components?: PayslipComponent[] | null
  }
  employee?: { firstName: string; lastName: string } | null
}

type RunData = {
  id: number
  periodStart: string
  periodEnd: string
  status: string | null
  totalGross?: string | null
  totalDeductions?: string | null
  totalNet?: string | null
  employeeCount?: number | null
  runAt?: Date | null
  runBy?: string | null
  notes?: string | null
}

function RunDetail() {
  const params = Route.useParams()
  const runId = Number(params.runId)
  const [expandedPayslips, setExpandedPayslips] = useState<Set<number>>(new Set())

  const queryClient = useQueryClient()

  const { data: runData, isLoading: runLoading } = useQuery(
    orpc.hr.getPayrollRun.queryOptions({ input: { id: runId } }),
  )

  const { data: payslipsData, isLoading: payslipsLoading } = useQuery(
    orpc.hr.listPayslips.queryOptions({ input: { payrollRunId: runId } }),
  )

  const run = runData as unknown as RunData | null | undefined
  const payslips = (payslipsData ?? []) as unknown as PayslipRow[]

  const processMutation = useMutation(
    orpc.hr.processPayrollRun.mutationOptions({
      onSuccess: () => {
        toast.success('Payroll run processed successfully')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getPayrollRun']] })
        queryClient.invalidateQueries({ queryKey: [['hr', 'listPayslips']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const cancelMutation = useMutation(
    orpc.hr.cancelPayrollRun.mutationOptions({
      onSuccess: () => {
        toast.success('Payroll run cancelled')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getPayrollRun']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const fmt = (val?: string | null) =>
    val ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'

  const togglePayslip = (id: number) => {
    setExpandedPayslips((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (runLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-48 w-full mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!run) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p className="text-muted-foreground">Payroll run not found</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/hr/payroll/runs">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Runs
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Payroll Run: {new Date(run.periodStart).toLocaleDateString()} —{' '}
            {new Date(run.periodEnd).toLocaleDateString()}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <StatusBadge status={run.status ?? 'draft'} />
            {run.runAt && (
              <span className="text-sm text-muted-foreground">
                Processed {(run.runAt instanceof Date ? run.runAt : new Date(run.runAt)).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {run.status === 'draft' && (
            <Button
              onClick={() => processMutation.mutate({ id: runId })}
              disabled={processMutation.isPending}
            >
              <Play className="h-4 w-4 mr-2" />
              {processMutation.isPending ? 'Processing...' : 'Process'}
            </Button>
          )}
          {(run.status === 'draft' || run.status === 'completed') && (
            <Button
              variant="destructive"
              onClick={() => cancelMutation.mutate({ id: runId })}
              disabled={cancelMutation.isPending}
            >
              <XCircle className="h-4 w-4 mr-2" />
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Employees</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{run.employeeCount ?? '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Gross</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{fmt(run.totalGross)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Deductions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{fmt(run.totalDeductions)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Net</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{fmt(run.totalNet)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Payslips Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payslips</CardTitle>
          <CardDescription>Individual payslips for this payroll run</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {payslipsLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : payslips.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {run.status === 'draft'
                  ? 'Process the run to generate payslips'
                  : 'No payslips found'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Employee</TableHead>
                  <TableHead>Base</TableHead>
                  <TableHead>Gross</TableHead>
                  <TableHead>Deductions</TableHead>
                  <TableHead>Net</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.map((row) => {
                  const ps = row.payslip
                  const isExpanded = expandedPayslips.has(ps.id)
                  const empName = row.employee
                    ? `${row.employee.firstName} ${row.employee.lastName}`
                    : `Employee #${ps.employeeId}`
                  const components = (ps.components ?? []) as PayslipComponent[]
                  const earningsComponents = components.filter((c) => c.type === 'earning' || !c.type)
                  const deductionComponents = components.filter((c) => c.type === 'deduction')

                  return (
                    <>
                      <TableRow
                        key={ps.id}
                        className="cursor-pointer"
                        onClick={() => togglePayslip(ps.id)}
                      >
                        <TableCell>
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{empName}</TableCell>
                        <TableCell>{fmt(ps.baseAmount)}</TableCell>
                        <TableCell>{fmt(ps.gross)}</TableCell>
                        <TableCell>{fmt(ps.totalDeductions)}</TableCell>
                        <TableCell>{fmt(ps.netPay)}</TableCell>
                        <TableCell>
                          <StatusBadge status={ps.status ?? 'draft'} />
                        </TableCell>
                      </TableRow>

                      {isExpanded && components.length > 0 && (
                        <TableRow key={`${ps.id}-expanded`}>
                          <TableCell />
                          <TableCell colSpan={6} className="bg-muted/30 py-3">
                            <div className="grid grid-cols-2 gap-4">
                              {earningsComponents.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-green-700 mb-1 uppercase tracking-wide">
                                    Earnings
                                  </p>
                                  {earningsComponents.map((c, i) => (
                                    <div key={i} className="flex justify-between text-sm py-0.5">
                                      <span className="text-muted-foreground">{c.name ?? c.code ?? 'Earning'}</span>
                                      <span className="font-medium">
                                        {(c.amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {deductionComponents.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-red-700 mb-1 uppercase tracking-wide">
                                    Deductions
                                  </p>
                                  {deductionComponents.map((c, i) => (
                                    <div key={i} className="flex justify-between text-sm py-0.5">
                                      <span className="text-muted-foreground">{c.name ?? c.code ?? 'Deduction'}</span>
                                      <span className="font-medium text-red-600">
                                        -{(c.amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {components.length === 0 && (
                                <p className="text-sm text-muted-foreground col-span-2">
                                  No component breakdown available
                                </p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {run.notes && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-sm">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm whitespace-pre-wrap">{run.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
